import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Users,
  Activity,
  ClipboardList,
  PlusCircle,
  Flag,
  PersonStanding,
  Calendar,
  UserPlus,
  TrendingUp,
  Target,
  MonitorUp,
} from 'lucide-react';
import PageLayout from '@/components/shared/PageLayout';
import KpiCard from './KpiCard';
import ActionBar from './ActionBar';
import HelpModal from '@/components/help-system/HelpModal';
import { getHelpContent } from '@/components/help-system/help-content';
import ListCard from './ListCard';
import { ClientInfoCard } from './ClientInfoCard/ClientInfoCard';
import { DashboardAccountMenu } from './DashboardAccountMenu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useClientsCache } from '@/context/clients-cache-context';
import { usePlan } from '@/context/plan-context';
import { useClient } from '@/context/client-context';
import { useSettings } from '@/components/settings/lib/settings-context';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { handleNewAssessment, handleStartAnalysis, handlePlanDrills, handleRemoteAssessment } from './lib/client-selection-handlers';
import { FeedbackButton } from '@/components/feedback-button/index';
import { getHelpKeyForPath } from './lib/help-utils';
import { safeToDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import logger from '@/lib/logging';
import {
  sumOpenAssessmentsFromSummaries,
  buildNeedsAttentionFromSummaries,
  buildAssessmentActivityFromSummaries,
  type DashboardWorkUrgency,
} from './lib/client-summaries';

function AttentionUrgencyBadge({ urgency }: { urgency: DashboardWorkUrgency }) {
  if (urgency === 'overdue') {
    return (
      <Badge
        className="shrink-0 border-status-warn bg-status-warn-surface text-status-warn hover:bg-status-warn-surface"
      >
        Due today
      </Badge>
    );
  }
  if (urgency === 'initial') {
    return (
      <Badge
        className="shrink-0 border-status-info bg-status-info-surface text-status-info hover:bg-status-info-surface"
      >
        Initial assessment
      </Badge>
    );
  }
  return (
    <Badge
      className="shrink-0 border-status-info bg-status-info-surface text-status-info hover:bg-status-info-surface"
    >
      Due soon
    </Badge>
  );
}

/**
 * Dashboard page composed of KPIs, primary actions, work queues and activity feeds.
 * Renders inside the shared PageLayout app shell.
 */
export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentHelpKey = getHelpKeyForPath(location.pathname);
  const { client: selectedClient } = useClient();
  const { programs } = usePlan();
  const isMobile = useIsMobile();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const helpContent = getHelpContent(currentHelpKey);
  const { settings } = useSettings();

  const { clients, clientSummaries, loading } = useClientsCache();

  const openAssessmentCount = useMemo(
    () => sumOpenAssessmentsFromSummaries(clientSummaries),
    [clientSummaries]
  );

  const kpis = useMemo(() => [
    { title: 'Clients', value: clients.length, icon: <Users className="h-5 w-5" /> },
    { title: 'Open Assessments', value: openAssessmentCount, icon: <ClipboardList className="h-5 w-5" /> },
    { title: 'Plans in Progress', value: programs.length, icon: <Activity className="h-5 w-5" /> },
    { title: 'Follow-ups Today', value: 0, icon: <Flag className="h-5 w-5" /> },
  ], [clients.length, openAssessmentCount, programs.length]);

  const needsAttention = useMemo(
    () => buildNeedsAttentionFromSummaries(clients, clientSummaries),
    [clients, clientSummaries]
  );

  const recentActivity = useMemo(() => {
    const items = buildAssessmentActivityFromSummaries(
      clients,
      clientSummaries,
      (date) => formatDistanceToNow(date, { addSuffix: true })
    );

    programs.slice(0, 3).forEach(program => {
      const date = safeToDate(program.updatedAt) || safeToDate(program.createdAt);

      if (date) {
        try {
          items.push({
            id: `program-${program.id ?? program.name}`,
            prefix: 'Updated drill plan: ',
            subject: program.name,
            subjectHref: '/drill-planner',
            timestamp: formatDistanceToNow(date, { addSuffix: true }),
          });
        } catch (error) {
          logger.warn(`Error formatting date for program: ${String(error)}`, { program });
        }
      }
    });

    return items.slice(0, 6);
  }, [clientSummaries, clients, programs]);

  const primaryActions = useMemo(() => {
    if (!selectedClient) return [];

    const actions = [
      {
        label: 'New Assessment',
        onClick: () => handleNewAssessment(navigate, selectedClient),
        icon: <PlusCircle className="h-5 w-5" />,
        variant: 'primary' as const,
      },
      {
        label: 'Start Analysis',
        onClick: () => handleStartAnalysis(navigate, selectedClient),
        icon: <PersonStanding className="h-5 w-5" />,
        variant: 'secondary' as const,
      },
    ];

    if (settings.experimentalFeaturesEnabled && !isMobile) {
      actions.push({
        label: 'Remote Assessment',
        onClick: () => handleRemoteAssessment(navigate, selectedClient),
        icon: <MonitorUp className="h-5 w-5" />,
        variant: 'secondary' as const,
      });
    }

    if (settings.experimentalFeaturesEnabled) {
      actions.push({
        label: 'Plan Drills',
        onClick: () => handlePlanDrills(navigate, selectedClient, isMobile),
        icon: <Calendar className="h-5 w-5" />,
        variant: 'secondary' as const,
      });
    }

    return actions;
  }, [navigate, selectedClient, settings.experimentalFeaturesEnabled, isMobile]);

  return (
    <>
      <PageLayout
        title="Dashboard"
        subtitle="Overview of clients, assessments and tasks"
        isLoading={loading}
        headerActions={
          <div className="flex w-full justify-end">
            <DashboardAccountMenu
              helpContent={helpContent}
              onOpenHelp={() => setIsHelpOpen(true)}
            />
          </div>
        }
      >
        {selectedClient ? (
          <ClientInfoCard
            client={selectedClient}
            onNavigateToClient={() => navigate(`/clients/${selectedClient.id}`)}
          />
        ) : (
          <div className="mb-4 sm:mb-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pb-2 sm:pb-0">
              {kpis.map((kpi, idx) => (
                <div key={idx} className="min-w-0">
                  <KpiCard
                    title={kpi.title}
                    value={kpi.value}
                    icon={kpi.icon}
                    onClick={() => {
                      if (kpi.title.includes('Clients')) navigate('/clients');
                      else if (kpi.title.includes('Assessments')) navigate('/sop-assessment');
                      else if (kpi.title.includes('Plans')) navigate('/drill-planner');
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          {!selectedClient ? (
            <Button
              onClick={() => navigate('/clients')}
              className="inline-flex h-12 items-center justify-center rounded-md bg-accent px-4 py-2 text-accent-fg transition hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
            >
              <UserPlus className="mr-2 h-5 w-5" />
              Select Client
            </Button>
          ) : (
            <ActionBar actions={primaryActions} />
          )}
        </div>

        <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-12 lg:items-start">
          <ListCard
            title="Needs Your Attention"
            items={needsAttention}
            itemsLayout="inset"
            getItemKey={(item) => item.clientId}
            renderItem={(item) => (
              <div className="grid w-full min-w-0 grid-cols-1 items-center gap-x-4 gap-y-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/clients/${item.clientId}`}
                      className="text-sm font-semibold leading-snug text-fg hover:text-accent sm:text-base"
                    >
                      {item.client}
                    </Link>
                    <AttentionUrgencyBadge urgency={item.urgency} />
                  </div>
                  <p className="text-sm leading-relaxed text-muted">{item.metaLine}</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="w-full justify-self-stretch sm:w-auto sm:justify-self-end"
                  onClick={() => navigate(`/clients/${item.clientId}`)}
                >
                  {item.actionLabel}
                </Button>
              </div>
            )}
            emptyState={<span className="text-status-clear">No urgent tasks</span>}
            className="min-w-0 h-fit lg:col-span-7 lg:col-start-1"
          />

          <ListCard
            title="Recent Activity"
            items={recentActivity}
            variant="flat"
            itemsLayout="divided"
            getItemKey={(item) => item.id}
            renderItem={(item) => (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                <p className="min-w-0 text-sm font-medium leading-relaxed text-fg sm:text-base">
                  {item.prefix}
                  {item.subjectHref ? (
                    <Link to={item.subjectHref} className="text-fg hover:text-accent hover:underline">
                      {item.subject}
                    </Link>
                  ) : (
                    item.subject
                  )}
                </p>
                <span className="shrink-0 text-xs text-muted sm:whitespace-nowrap">
                  {item.timestamp}
                </span>
              </div>
            )}
            emptyState={<span>No recent activity</span>}
            className={cn('min-w-0 h-fit lg:col-span-5 lg:col-start-8 lg:min-h-[12rem]')}
          />
        </div>

        <section
          aria-label="Learning resources"
          className="mt-6 rounded-lg border border-line bg-surface shadow-sm lg:hidden"
        >
          <div className="border-b border-line px-4 py-4">
            <h2 className="text-base font-semibold text-fg">Resources</h2>
          </div>
          <div className="flex flex-col gap-1 p-2">
            <Link to="/progression-model" className="block">
              <Button
                variant="ghost"
                className="h-12 w-full justify-start gap-3 px-3 text-fg hover:bg-bg hover:text-accent"
              >
                <TrendingUp className="h-5 w-5 shrink-0 text-muted" aria-hidden />
                Progression Model
              </Button>
            </Link>
            <Link to="/coaching" className="block">
              <Button
                variant="ghost"
                className="h-12 w-full justify-start gap-3 px-3 text-fg hover:bg-bg hover:text-accent"
              >
                <Target className="h-5 w-5 shrink-0 text-muted" aria-hidden />
                Coaching Cues
              </Button>
            </Link>
          </div>
        </section>
      </PageLayout>

      {helpContent && (
        <HelpModal
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          content={helpContent}
        />
      )}

      <div className="sr-only" aria-hidden>
        <FeedbackButton />
      </div>
    </>
  );
};

export default Dashboard;
